import { Observable, Subject } from 'rxjs';
import { BaseEditor, Editor, EditorServices, EditorSpec } from './editor';

/**
 * Editor that connects/disconnects to/from different targets over time, such as using a React ref.
 */
export interface ConnectingEditor<T, S extends EditorServices> extends Editor<S> {
  readonly target$: Subject<T | null>;

  /** Can be used as a React ref to connect on mount and disconnect on unmount. */
  readonly targetRef: (target: T | null) => void;
}

export type ConnectingEditorSpecCreator<T, S extends EditorServices> = (
  target$: Observable<T | null>,
) => EditorSpec<S>;

export function createConnectingEditor<T, S extends EditorServices>(
  createSpec: ConnectingEditorSpecCreator<T, S>,
): ConnectingEditor<T, S> {
  return new BaseConnectingEditor(createSpec);
}

export class BaseConnectingEditor<T, S extends EditorServices>
  extends BaseEditor<S>
  implements ConnectingEditor<T, S> {
  constructor(createSpec: ConnectingEditorSpecCreator<T, S>) {
    const target$ = new Subject<T | null>();

    super(createSpec(target$));

    this.target$ = target$;
    this.targetRef = (target: T | null) => this.target$.next(target);
  }

  readonly target$: Subject<T | null>;
  readonly targetRef: (target: T | null) => void;
}
